/**
 * Session and cookie storage management
 * Unified interface for managing session storage and cookies with automatic expiry
 */

import Cookies from 'js-cookie'
import { generateID, globalState } from './utils'
import type {
  StorageItem,
  SessionKeyDefinitions,
  StorageItemValue
} from './session.types'

/**
 * Base class for managing values in both SessionStorage and Cookies
 */
class StorageValue implements StorageItem {
  protected key: string
  protected expires: number
  protected aliasKey?: string

  constructor(key: string, expires = 0, aliasKey?: string) {
    this.key = key
    this.expires = expires
    this.aliasKey = aliasKey
  }

  get(delimiter = ''): string | string[] | null {
    let value: string | null = Cookies.get(this.key) || null
    if (!value) {
      // Fall back to SessionStorage, then a legacy-named cookie (e.g. ssUserId
      // written by the Shopify pixel or the previous theme), and re-set so the
      // canonical cookie exists and both integrations share the same IDs
      const fallback =
        sessionStorage.getItem(this.key) ||
        (this.aliasKey && Cookies.get(this.aliasKey)) ||
        null
      if (fallback) {
        value = this.set(fallback) as string
      }
    }
    if (typeof value === 'string' && delimiter) {
      return value.split(delimiter)
    }
    return value
  }

  set(value: StorageItemValue): StorageItemValue {
    if (!value) {
      Cookies.remove(this.key)
      if (this.aliasKey) Cookies.remove(this.aliasKey)
      sessionStorage.removeItem(this.key)
    } else {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value)
      // expires: 0 means a session-scoped cookie — js-cookie treats a numeric 0
      // as "expire now", so the expires option must be omitted entirely
      const options = this.expires ? { expires: this.expires } : {}
      Cookies.set(this.key, stringValue, options)
      if (this.aliasKey) Cookies.set(this.aliasKey, stringValue, options)
      sessionStorage.setItem(this.key, stringValue)
    }
    return value
  }
}

/**
 * Class for managing ID values with automatic generation
 */
class StorageID extends StorageValue {
  constructor(key: string, expires = 0, aliasKey?: string) {
    super(key, expires, aliasKey)
    this.get() // Initialize ID if not exists
  }

  get(): string {
    let id = super.get() as string | null
    if (!id) {
      id = this.regenerate()
    }
    return id
  }

  regenerate(): string {
    return super.set(this.generate()) as string
  }

  protected generate(): string {
    return generateID()
  }
}

/**
 * Main Session class for managing various IDs and values
 */
class Session {
  [key: string]: StorageItem | (() => void); // Allow dynamic properties while maintaining type safety

  /**
   * Athos first-party cookies.
   * `expires: 0` = session-scoped cookie. `aliasId` mirrors the value under the
   * legacy SearchSpring cookie name so the Shopify pixel (searchspring/shopify/pixel)
   * reports the same identity as this integration.
   */
  static readonly KEY_DEFINITIONS: SessionKeyDefinitions = {
    pageLoadId: {
      id: 'athosPageLoadId',
      aliasId: 'ssPageLoadId',
      expires: 0,
      generateIfEmpty: true,
      regenerateOnLoad: true
    },
    shopper: { id: 'athosShopperId', expires: 365, generateIfEmpty: false },
    sessionId: {
      id: 'athosSessionId',
      aliasId: 'ssSessionIdNamespace',
      expires: 0,
      generateIfEmpty: true
    },
    userId: {
      id: 'athosUserId',
      aliasId: 'ssUserId',
      expires: 365,
      generateIfEmpty: true
    },
    viewedProducts: {
      id: 'athosViewedProducts',
      expires: 365 * 7,
      delimiter: ',',
      generateIfEmpty: false
    },
    columnPreferences: {
      id: 'athosColumnPreferences',
      expires: 0,
      generateIfEmpty: false
    }
  }

  pageLoadId!: StorageID
  shopper!: StorageValue
  sessionId!: StorageID
  userId!: StorageID
  viewedProducts!: StorageValue
  columnPreferences!: StorageValue

  constructor() {
    this.initializeStorageItems()
    this.setShopperID()
    this.initEventListeners()
  }

  /**
   * BFCache restores (back/forward navigation) don't re-run module top-level
   * code, so the singleton's constructor-time pageLoadId regeneration never
   * fires — without this listener the same pageLoadId leaks across pages.
   */
  protected initEventListeners(): void {
    window.addEventListener('pageshow', (e: PageTransitionEvent) => {
      if (e.persisted) {
        this.pageLoadId.regenerate()
      }
    })
  }

  protected initializeStorageItems(): void {
    for (const [key, value] of Object.entries(Session.KEY_DEFINITIONS)) {
      const StorageClass = value.generateIfEmpty ? StorageID : StorageValue
      this[key] = new StorageClass(value.id, value.expires, value.aliasId)
      if (value.regenerateOnLoad) {
        // pageLoadId must be unique per page load, not per session
        (this[key] as StorageID).regenerate()
      } else if (value.generateIfEmpty) {
        (this[key] as StorageID).get() // This will trigger ID generation if it doesn't exist
      }
    }
  }

  protected setShopperID(): void {
    const customerID = globalState?.customer?.id

    if (customerID) {
      this.shopper.set(customerID)
    } else {
      // delete shopper if customer is not logged in
      this.shopper.set(null)
    }
  }
}

// Export a singleton instance
export const session = new Session()
